import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Share2, Award, Check, ArrowRight } from 'lucide-react';
import { Button } from '../ui/Button';
import { DjayIdentity, DjaySpecimen, useDjaySelection, getAchievementIllustration } from '../../mascot';
import { ACHIEVEMENTS } from '../progress/AchievementsSection';
import { ACCESSORY_NAMES } from '../../mascot/useDjayCustomization';
import { ConfettiCelebration } from '../../accounts/components/ConfettiCelebration';
import { playSoundIntent } from '../../utils/sound/soundIntent';
import { hapticManager } from '../../utils/hapticManager';
import { useMotionBudget } from '../../app/hooks/useMotionBudget';
export interface AchievementData {
  id: string;
  name: string;
  description: string;
  emoji: string;
  rarity: 'common' | 'uncommon' | 'rare' | 'legendary';
}
interface AchievementUnlockModalProps {
  achievement: AchievementData | null;
  onClose: () => void;
  /**
   * When set, this unlock coincided with a level-up. The modal then doubles as
   * the level-up celebration so the two never fire as competing popups. The
   * parent (AchievementCeremony) clears pendingLevelUp on close.
   */
  levelUpTo?: number | null;
  /**
   * Wayfinding callback — when provided, surfaces a "Bekijk in Collectie"
   * action so a just-earned reward is always findable. The parent handles the
   * actual navigation (and modal teardown); this modal only seeds the
   * `collectionFocus` handshake so the Collection deep-focuses the reward.
   */
  onNavigateToCollection?: () => void;
}
export function AchievementUnlockModal({
  achievement,
  onClose,
  levelUpTo,
  onNavigateToCollection
}: AchievementUnlockModalProps) {
  const {
    shouldAnimate
  } = useMotionBudget();
  const {
    setSelectedVariant,
    setSelectedAccessory
  } = useDjaySelection();
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (achievement) {
      if (levelUpTo) {
        // Level-up + achievement: play the level fanfare first, then the
        // achievement chime so they read as one choreographed moment.
        playSoundIntent('reward.levelUp');
        const t = setTimeout(() => playSoundIntent('reward.achievement'), 300);
        hapticManager.hapticCelebration();
        setCopied(false);
        return () => clearTimeout(t);
      }
      playSoundIntent('reward.achievement');
      // F-FEEL-2: pair the unlock fanfare with a celebration buzz on mobile
      hapticManager.hapticCelebration();
      setCopied(false);
    }
  }, [achievement, levelUpTo]);
  if (!achievement) return null;
  const rarityColors = {
    common: 'from-slate-200 to-slate-300 text-slate-700 border-slate-300',
    uncommon: 'from-emerald-200 to-emerald-300 text-emerald-800 border-emerald-300',
    rare: 'from-blue-300 to-indigo-400 text-white border-blue-400',
    legendary: 'from-amber-300 via-orange-400 to-rose-400 text-white border-amber-400'
  };
  const rarityLabels = {
    common: 'Gewoon',
    uncommon: 'Bijzonder',
    rare: 'Zeldzaam',
    legendary: 'Legendarisch!'
  };
  const Illustration = achievement ? getAchievementIllustration(achievement.id) : null;
  const fullAchievement = ACHIEVEMENTS.find(a => a.id === achievement.id);
  const grantedVariant = fullAchievement?.djayVariantUnlocked;
  const grantedAccessory = fullAchievement?.accessory && fullAchievement.accessory !== 'none' ? fullAchievement.accessory : null;
  const hasReward = grantedVariant || grantedAccessory;
  const handleEquip = () => {
    if (grantedVariant) setSelectedVariant(grantedVariant);
    if (grantedAccessory) setSelectedAccessory(grantedAccessory);
    // Remember the just-equipped reward so the Collection deep-focuses (scrolls
    // + pulses) it the next time the learner opens that page — mirrors the
    // GhostTrophy → Collection wayfinding so rewards are always findable.
    const focusId = grantedVariant || grantedAccessory;
    if (focusId) {
      try {
        sessionStorage.setItem('collectionFocus', focusId);
      } catch {
        /* sessionStorage unavailable — non-critical */
      }
    }
    onClose();
  };
  // Wayfinding: equip the reward (if any), seed the deep-focus handshake, then
  // let the parent navigate to the Collection page.
  const handleViewInCollection = () => {
    if (grantedVariant) setSelectedVariant(grantedVariant);
    if (grantedAccessory) setSelectedAccessory(grantedAccessory);
    const focusId = grantedVariant || grantedAccessory;
    if (focusId) {
      try {
        sessionStorage.setItem('collectionFocus', focusId);
      } catch {
        /* sessionStorage unavailable — non-critical */
      }
    }
    onNavigateToCollection?.();
  };
  return <AnimatePresence data-id="element-2254" data-mp="2ytxnlp6ndcum">
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6" data-id="element-2255" data-mp="2yufhwg9r1gzw">
        {/* Backdrop */}
        <motion.div initial={shouldAnimate ? {
        opacity: 0
      } : false} animate={shouldAnimate ? {
        opacity: 1
      } : false} exit={shouldAnimate ? {
        opacity: 0
      } : false} onClick={onClose} className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" data-id="element-2256" data-mp="10hfiemzi3je6" />

        {/* Confetti */}
        <div className="absolute inset-0 pointer-events-none z-0" data-id="element-2257" data-mp="1fvhuil5yvtm6">
          <ConfettiCelebration data-id="element-2258" data-mp="1ay9bkiczvljv" />
        </div>

        {/* Modal Content */}
        <motion.div initial={shouldAnimate ? {
        opacity: 0,
        scale: 0.8,
        y: 20
      } : false} animate={shouldAnimate ? {
        opacity: 1,
        scale: 1,
        y: 0
      } : false} exit={shouldAnimate ? {
        opacity: 0,
        scale: 0.9,
        y: -20
      } : false} transition={{
        type: 'spring',
        bounce: 0.5,
        duration: 0.6
      }} className="relative z-10 w-full max-w-md bg-white rounded-3xl shadow-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden" data-id="element-2259" data-mp="16v38fjl6iahm">
          {/* Header Pattern */}
          <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-br from-indigo-500 to-purple-600 opacity-10" data-id="element-2260" data-mp="3a4en1q8l5dy4" />

          <button onClick={onClose} className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-full transition-colors z-20" aria-label="Sluiten" data-id="element-2261" data-mp="2vgjrm2370vhc">
            <X className="w-5 h-5" data-id="element-2262" data-mp="2mgwsxo1um3rz" />
          </button>

          <div className="p-8 flex flex-col items-center text-center relative z-10" data-id="element-2263" data-mp="2rcc3a60ndhjn">
            {/* Djay Avatar */}
            <div className="w-24 h-24 mb-6 relative" data-id="element-2264" data-mp="27s3s9c1315p0">
              <motion.div animate={shouldAnimate ? {
              y: [0, -10, 0]
            } : false} transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-2265" data-mp="22taav8iocujk">
                <DjayIdentity emotion="celebrating" size="xl" animate={shouldAnimate} data-id="element-2266" data-mp="2lrwd4qpxgrp2" />
              </motion.div>
            </div>

            {levelUpTo ? <div className="flex flex-col items-center gap-1 mb-2" data-id="element-2267" data-mp="2vihbqe5lpw7z">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-indigo-100 text-indigo-700 text-xs font-black uppercase tracking-wider" data-id="element-2268" data-mp="39gxpuq5smp05">
                  <span data-id="element-2269" data-mp="34g1fpuuum6ma">⭐</span> Level {levelUpTo} bereikt!
                </span>
                <h2 className="text-sm font-bold text-indigo-600 uppercase tracking-wider" data-id="element-2270" data-mp="fony2x92vqfu">
                  + Nieuwe Prestatie!
                </h2>
              </div> : <h2 className="text-sm font-bold text-indigo-600 uppercase tracking-wider mb-2" data-id="element-2271" data-mp="or5h72tfs2ym">
                Nieuwe Prestatie!
              </h2>}

            {/* Illustration or Badge */}
            {Illustration ? <motion.div initial={shouldAnimate ? {
            scale: 0,
            rotate: -5
          } : false} animate={shouldAnimate ? {
            scale: 1,
            rotate: 0
          } : false} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15,
            delay: 0.2
          }} className="w-full aspect-video rounded-2xl overflow-hidden mb-6 shadow-xl border-4 border-white relative flex items-center justify-center" data-id="element-2272" data-mp="3t2945zgl0bxx">
                <Illustration achievementId={achievement.id} isUnlocked={true} rarity={achievement.rarity} size="lg" data-id="element-2273" data-mp="2sw5lm5gpl0ap" />
              </motion.div> : <motion.div initial={shouldAnimate ? {
            scale: 0,
            rotate: -180
          } : false} animate={shouldAnimate ? {
            scale: 1,
            rotate: 0
          } : false} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15,
            delay: 0.2
          }} className={`w-24 h-24 rounded-2xl bg-gradient-to-br ${rarityColors[achievement.rarity]} border-4 shadow-xl flex items-center justify-center text-5xl mb-6 relative overflow-hidden`} data-id="element-2274" data-mp="2j4hrtxwoj762">
                {/* Shine effect */}
                {shouldAnimate && <motion.div animate={{
              x: ['-100%', '200%']
            }} transition={{
              duration: 1.5,
              repeat: Infinity,
              repeatDelay: 3,
              ease: 'easeInOut'
            }} className="absolute inset-0 w-1/2 bg-white/30 skew-x-12" data-id="element-2275" data-mp="l3a51eeqkb9r" />}
                <span className="drop-shadow-md" data-id="element-2276" data-mp="26g3fqa1hhdth">{achievement.emoji}</span>
              </motion.div>}

            <h3 className="text-2xl font-black text-slate-800 mb-2" data-id="element-2277" data-mp="2dyt6ekcimk3g">
              {achievement.name}
            </h3>
            <p className="text-slate-600 mb-4" data-id="element-2278" data-mp="2iwur19plarh1">{achievement.description}</p>

            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-100 text-slate-600 text-xs font-bold uppercase tracking-wider mb-6" data-id="element-2279" data-mp="2519190bj83nk">
              <Award className="w-3.5 h-3.5" data-id="element-2280" data-mp="2wqcakuia0472" />
              {rarityLabels[achievement.rarity]}
            </div>

            {hasReward && <div className="w-full bg-amber-50 border-2 border-amber-200 rounded-2xl p-4 mb-8 flex flex-col items-center" data-id="element-2281" data-mp="31rt60ahzqyg9">
                <span className="text-xs font-bold text-amber-600 uppercase tracking-wider mb-2" data-id="element-2282" data-mp="2rznhjaqy6z5t">
                  Je hebt een nieuwe look ontgrendeld!
                </span>
                <div className="w-20 h-20 bg-white rounded-full border-2 border-amber-200 flex items-center justify-center mb-2 shadow-sm overflow-hidden" data-id="element-2283" data-mp="3fndhwlzayge6">
                  <DjaySpecimen size="xl" emotion="proud" look={{
                variant: grantedVariant || 'default',
                accessory: grantedAccessory || 'none'
              }} animate={false} data-id="element-2284" data-mp="3ub5fbfpanw9z" />
                </div>
                <span className="font-black text-slate-800" data-id="element-2285" data-mp="3gerv3ytdlgrt">
                  {grantedVariant ? 'Nieuwe Kleur' : ACCESSORY_NAMES[grantedAccessory!]}
                </span>
              </div>}

            <div className="w-full flex flex-col gap-3" data-id="element-2286" data-mp="32m29hvhredv8">
              {hasReward ? <div className="flex gap-3" data-id="element-2287" data-mp="2xmpbcelftkdc">
                  <Button onClick={onClose} variant="outline" size="lg" className="flex-1 font-bold" data-id="element-2288" data-mp="2xnqwxd02xp9b">
                    Verder
                  </Button>
                  <Button onClick={handleEquip} size="lg" className="flex-1 font-bold bg-amber-500 hover:bg-amber-600 text-white" data-id="element-2289" data-mp="2dkkzyt2j2rqb">
                    Aandoen
                  </Button>
                </div> : <Button onClick={onClose} size="lg" className="w-full font-bold" data-id="element-2290" data-mp="2sg0zvepshhum">
                  Geweldig!
                </Button>}

              {onNavigateToCollection && <Button variant="ghost" onClick={handleViewInCollection} className="w-full font-bold text-indigo-600 hover:text-indigo-700" data-id="element-2291" data-mp="24rhp8sj9oszd">
                  Bekijk in Collectie
                  <ArrowRight className="w-4 h-4 ml-2" data-id="element-2292" data-mp="1c2av05quipjx" />
                </Button>}

              <Button variant="ghost" className={`w-full ${copied ? 'text-emerald-600 hover:text-emerald-700' : 'text-slate-500 hover:text-slate-700'}`} onClick={() => {
              const text = `🏆 Ik heb de prestatie "${achievement.name}" behaald in Meester Djay! ${achievement.emoji}`;
              navigator.clipboard.writeText(text).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }).catch(() => {
                // Fallback for older browsers
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              });
            }} data-id="element-2293" data-mp="1h0m1q7lugjxx">
                {copied ? <>
                    <Check className="w-4 h-4 mr-2" data-id="element-2294" data-mp="2h0xpks9rco9u" />
                    Gekopieerd!
                  </> : <>
                    <Share2 className="w-4 h-4 mr-2" data-id="element-2295" data-mp="2c4qobcsb40y8" />
                    Deel met leerkracht
                  </>}
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>;
}